import * as R from 'ramda';
import { lockedServiceUtils } from 'utilities/LockedServiceUtils';

import * as ServiceTypes from 'reactApp/modules/ServiceModule/ServiceModule.types';

const API_SERVICE_NAME_TO_INTERNAL = {
    hotbox: 'storage',
    vision: 'machineLearning',
};
const SERVICE_NAMES = {
    infra: 'облачных вычислений',
    databases: 'баз данных',
    containers: 'контейнеров',
    marketplace: 'магазина приложений',
    storage: 'объектного хранилища',
    bigdata: 'больших данных',
    gpu: 'графических адапторов',
    machineLearning: 'машинного обучения',
};
const INTERNAL_SERVICE_NAME_TO_API = R.invertObj(API_SERVICE_NAME_TO_INTERNAL);
const transformApiServiceNameToInternal = (
    name: ServiceTypes.ApiServiceName,
): ServiceTypes.InternalServiceName => {
    return API_SERVICE_NAME_TO_INTERNAL[name] || name;
};
const transformInternalServiceNameToApi = (
    name: ServiceTypes.InternalServiceName,
): ServiceTypes.ApiServiceName => {
    return (INTERNAL_SERVICE_NAME_TO_API[name] ||
        name) as ServiceTypes.ApiServiceName;
};

const isActivated = (service: ServiceTypes.ServiceData) => {
    return service.status && service.status !== 'UNACTIVATED';
};

const isUnactivated = (service: ServiceTypes.ServiceData) => {
    return !isActivated(service);
};

const isFrozen = (service: ServiceTypes.ServiceData) => {
    return service.status === 'FROZEN';
};
const isSuspended = (service: ServiceTypes.ServiceData) => {
    return service.status === 'SUSPENDED';
};
const getSuspendDateString = (service: ServiceTypes.ServiceData) => {
    return formatSuspendDate((service as ServiceTypes.InfraData).suspended_at);
};

function formatSuspendDate(suspendDate: number) {
    const suspendedTime = 30 * 24 * 60 * 60 * 1000;
    const date = new Date(suspendDate * 1000 + suspendedTime);

    return lockedServiceUtils.getFormattedDate(date);
}
const getStatusMessage = ({
    isFrozen,
    suspendDate,
    humanReadableName,
}: ServiceTypes.StatusMessageData) => {
    return isFrozen
        ? humanReadableName
            ? `Вы не можете создавать и использовать ресурсы сервиса ${SERVICE_NAMES[humanReadableName]}. Пополните баланс, чтобы возобновить работу.`
            : 'Вы не можете создавать и использовать ресурсы некоторых сервисов. Пополните баланс, чтобы возобновить работу.'
        : `Для продолжения работы с сервисом и восстановления работоспособности всех методов пополните счёт. Если этого не произойдёт до ${suspendDate}, то все ваши данные будут удалены.`;
};
const isProtected = (service: ServiceTypes.ServiceData) => {
    // @ts-ignore
    return service.protected;
};

function getMinSuspendedDate(suspendedServices: ServiceTypes.Service[]) {
    return ServiceUtils.formatSuspendDate(
        Math.min(
            ...suspendedServices.map(
                (suspendedService) => suspendedService.suspendedDate,
            ),
        ),
    );
}

enum HumanServiceNames {
    infra = 'Облачные вычисления',
    containers = 'Кластеры Kubernetes',
    bigdata = 'Большие данные',
    databases = 'Базы данных',
    gpu = 'Облачная GPU-инфраструктура',
    machineLearning = 'Машинное обучение',
    storage = 'Объектное хранилище',
    marketplace = 'Магазин приложений',
}

const internalServiceNameToMetricName: Record<
    ServiceTypes.InternalServiceName,
    string
> = {
    storage: 'CloudStorage',
    bigdata: 'Bigdata',
    containers: 'Containers',
    databases: 'Databases',
    analyticDatabases: 'AnalyticDatabases',
    gpu: 'Gpu',
    infra: 'Infra',
    machineLearning: 'MachineLearning',
    marketplace: 'Marketplace',
};

const internalServiceNameToId: Record<ServiceTypes.ApiServiceName, string> = {
    infra: '#infra-main',
    containers: '#containers-main',
    databases: '#databases-main',
    analyticDatabases: '#analytic-databases-main',
    bigdata: '#bigdata-main',
    gpu: '#gpu-main',
    hotbox: '#storage-main',
    marketplace: '#marketplace-main',
    vision: '#machine-learning-main',
};

const internalServiceNameToYandexMetricName: Partial<Record<
    ServiceTypes.InternalServiceName,
    string
>> = {
    storage: 'cloudstorage_connect',
    bigdata: 'cloudbigdata_connect',
    containers: 'cloudcontainers_connect',
    databases: 'clouddatabases_connect',
    gpu: 'cloudgpu_connect',
};

export const ServiceUtils = {
    internalServiceNameToId,
    internalServiceNameToMetricName,
    internalServiceNameToYandexMetricName,
    HumanServiceNames,
    isUnactivated,
    isActivated,
    isFrozen,
    isSuspended,
    isProtected,
    getSuspendDateString,
    getStatusMessage,
    getMinSuspendedDate,

    formatSuspendDate,

    transformApiServiceNameToInternal,
    transformInternalServiceNameToApi,
};
